import { Plane, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  buildCalendar,
  shortDate,
  WEEKDAY_INITIALS,
  type CalendarDay,
  type CalendarEvent,
  type CalendarFlight,
} from "./tripCalendarView";

interface Props {
  flights: CalendarFlight[];
  events: CalendarEvent[];
  /** Today as a calendar date (YYYY-MM-DD). */
  today?: string | null;
  weekStart?: 0 | 1;
  /** Offered only while no event day is known. */
  onAddEvent?: () => void;
  /** Writes a proposed (unconfirmed) event. Never called without a tap. */
  onConfirmEvent?: (e: CalendarEvent) => void;
  confirming?: boolean;
}

function ariaFor(d: CalendarDay): string {
  const marks = d.marks.map((m) => m.label).join(", ");
  return `${shortDate(d.date)}${marks ? `: ${marks}` : ""}${d.today ? " (today)" : ""}`;
}

/** Compact week strip: which weekday is the flight, which is the event. */
export default function TripWeekCalendar({ flights, events, today, weekStart = 0, onAddEvent, onConfirmEvent, confirming }: Props) {
  const model = buildCalendar({ flights, events, today, weekStart });
  if (!model) return null;
  const noEvents = events.length === 0;
  const pendingEvents = events.filter((e) => e.unconfirmed);

  return (
    <section aria-label="Trip calendar" className="space-y-2" data-testid="trip-week-calendar">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
        <p className="text-sm font-medium">{model.caption}</p>
        {noEvents && onAddEvent && (
          <button
            type="button"
            onClick={onAddEvent}
            className="inline-flex items-center gap-1 rounded text-xs text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Plus className="h-3 w-3" aria-hidden /> Add event day
          </button>
        )}
      </div>
      <div className={cn("space-y-1", model.layout === "weeks" ? "max-w-md" : "max-w-xl")}>
        {model.weeks.map((week) => (
          <ul key={week[0].date} className="grid" style={{ gridTemplateColumns: `repeat(${week.length}, minmax(0, 1fr))` }}>
            {week.map((d) => {
              const kinds = new Set(d.marks.map((m) => m.kind));
              const flight = kinds.has("flight");
              const ev = d.marks.find((m) => m.kind === "event");
              const event = !!ev;
              const pending = !!ev?.pending && ev.pending;
              const alt = kinds.has("alt");
              const title = d.marks.map((m) => m.label).join(", ");
              return (
                <li
                  key={d.date}
                  title={title || undefined}
                  aria-label={d.marks.length || d.today ? ariaFor(d) : undefined}
                  className="flex flex-col items-center py-0.5"
                >
                  <span className="text-[10px] leading-4 text-muted-foreground" aria-hidden>
                    {WEEKDAY_INITIALS[d.weekday]}
                  </span>
                  <span
                    className={cn(
                      "flex aspect-square w-full max-w-8 items-center justify-center rounded-full text-xs tabular-nums",
                      !d.inWindow && "text-muted-foreground/60",
                      flight && "bg-primary font-semibold text-primary-foreground",
                      !flight && event && !pending && "bg-amber-100 font-semibold text-amber-900 dark:bg-amber-950 dark:text-amber-200",
                      !flight && event && pending && "border border-dotted border-amber-600 font-semibold text-amber-900 dark:border-amber-400 dark:text-amber-200",
                      !flight && !event && alt && "border border-dotted border-muted-foreground text-foreground",
                      d.today && !flight && !event && "ring-1 ring-foreground/60",
                      flight && event && "ring-2 ring-amber-500",
                    )}
                  >
                    {flight ? <Plane className="h-3.5 w-3.5" aria-hidden /> : d.dayOfMonth}
                  </span>
                  <span aria-hidden className={cn("mt-1 h-0.5 w-full", d.inStay ? "bg-primary/50" : "bg-transparent")} />
                  {ev && (
                    <span className="hidden max-w-full truncate text-[10px] text-amber-800 dark:text-amber-300 sm:block" aria-hidden>
                      {ev.label}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        ))}
      </div>
      {pendingEvents.map((e) => (
        <div key={e.date + e.title} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          <span>
            {e.title}, {shortDate(e.date)}, found in your trip notes
          </span>
          {onConfirmEvent && (
            <button
              type="button"
              disabled={confirming}
              onClick={() => onConfirmEvent(e)}
              className="rounded font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
            >
              {confirming ? "Adding..." : "Confirm"}
            </button>
          )}
        </div>
      ))}
    </section>
  );
}
